import React, { useContext, useMemo, useCallback } from 'react';
import { Field, useForm } from 'react-final-form';

export const FieldNameContext = React.createContext(null);

export const useFieldName = () => useContext(FieldNameContext);

export const useWrappedHandler = (rffHandler, customHandler, additionalHandler) =>
    useMemo(() => (...args) => {
        rffHandler(...args);
        customHandler && customHandler(...args);
        additionalHandler && additionalHandler(...args);
    }, [rffHandler, customHandler, additionalHandler]);

export const useWrappedInput = (input, onChange, onBlur, onFocus) =>
    useMemo(() => ({
        ...input,
        onChange,
        onBlur,
        onFocus
    }), [input, onBlur, onChange, onFocus]);

export const useWrappedHandlers = ({
    type,
    input,
    onChange: onChangeCustom,
    onBlur: onBlurCustom,
    onFocus: onFocusCustom
}) => {
    const {
        onChange: onChangeRff,
        onBlur: onBlurRff,
        onFocus: onFocusRff
    } = input;

    const onBlur = useWrappedHandler(onBlurRff, onBlurCustom);
    const onFocus = useWrappedHandler(onFocusRff, onFocusCustom);
    // Fix for iOS: no blur event for checkbox and radio so final-form cannot perform validation.
    // We need to fire blur handler manually after change event.
    const onChange = useWrappedHandler(onChangeRff, onChangeCustom, ['checkbox', 'radio'].includes(type) ? onBlur : null);

    return useWrappedInput(input, onChange, onBlur, onFocus);
};

/**
 * @typedef {Object} WithFieldProps
 * @property [afterSubmit] {Function}
 * @property [allowNull] {boolean}
 * @property [beforeSubmit] {Function}
 * @property [component] {JSX.Element}
 * @property [data] {Object}
 * @property [defaultValue] {*}
 * @property [enableSelfValueChange] {boolean}
 * @property [format] {Function}
 * @property [formatOnBlur] {boolean}
 * @property [initialValue] {*}
 * @property [isEqual] {Function}
 * @property [multiple] {boolean}
 * @property [onChange] {Function}
 * @property [onBlur] {Function}
 * @property [onfocus] {Function}
 * @property [parse] {Function}
 * @property [render] {() => JSX.Element}
 * @property [ref] {React.MutableRefObject}
 * @property [subscription] {Object<name, boolean>}
 * @property [validate] {Function}
 * @property [validateFields] {string[]}
 * @property [value] {*}
 */

export const withField = Wrapped => {

    /**
     * @param props {WithFieldProps & Object<string, *>}
     */
    return ({
        // RFF Field props
        afterSubmit,
        allowNull,
        beforeSubmit,
        component, // silently omitting since we already use Field's children for rendering
        data,
        defaultValue,
        format,
        formatOnBlur,
        initialValue,
        isEqual,
        multiple,
        name,
        parse,
        ref,
        render, // silently omitting since we already use Field's children for rendering
        subscription,
        type,
        validate,
        validateFields,
        value, // only for type=checkbox & type=radio (see RFF docs for more info)

        onChange,
        onBlur,
        onFocus,
        enableSelfValueChange,
        ...componentRestProps
    }) => {
        // omit `children`, `component` and `render` - user cannot change render method for RFF Field
        const rffFieldProps = {
            afterSubmit,
            allowNull,
            beforeSubmit,
            data,
            defaultValue,
            format,
            formatOnBlur,
            initialValue,
            isEqual,
            multiple,
            name,
            parse,
            ref,
            subscription,
            type,
            validate,
            validateFields,
            value
        };
        const { change } = useForm();
        const setValue = useCallback((val) => change(name, val), [change, name]);
        const selfChangeProps = enableSelfValueChange ? {
            parse,
            setValue
        } : {};

        return (
            <Field {...rffFieldProps}>
                {(renderProps) => {
                    const { input } = renderProps;
                    const wrappedInput = useWrappedHandlers({ type, input, onChange, onBlur, onFocus });

                    return (
                        <FieldNameContext.Provider value={name}>
                            <Wrapped
                                {...{
                                    ...componentRestProps,
                                    ...renderProps, /* { input, meta } */
                                    ...selfChangeProps,
                                    name,
                                    type,
                                    input: wrappedInput
                                }}
                            />
                        </FieldNameContext.Provider>
                    );
                }}
            </Field>
        );
    };
};

export default withField;
